/**
 * Application detail — Overview / Resources / Logs and Metrics / Runtime Alerts.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Checkbox,
  Collapse,
  Modal,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from "antd";
import {
  ArrowLeftOutlined,
  AppstoreOutlined,
  BellOutlined,
  CloudServerOutlined,
  FileTextOutlined,
  LineChartOutlined,
  PlusOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import IsEnabled from "../rolepermission";
import { applicationDiscoveryService } from "../services/applicationDiscoveryService";
import { alertApi } from "../services/alertApi";
import {
  alertMatchesApplication,
  appDisplayName,
  coverageDisplayLabel,
  coverageTagColor,
  formatServiceKey,
  getApplicationOnboardingState,
  getDominantServiceType,
  getResourceSummary,
  logTargetDisplayName,
  logTargetSourceLabel,
  metricTargetDisplayName,
  resolveResourceServiceKey,
  resourceDisplayName,
  serviceTypeTagColor,
} from "../utils/applicationDiscoveryHelpers";

const { Text, Title } = Typography;

function normalizeFiringList(payload) {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload.alerts)) return payload.alerts;
  if (Array.isArray(payload.data)) return payload.data;
  if (Array.isArray(payload.groups)) {
    const rows = [];
    for (const g of payload.groups) {
      for (const alert of g.alerts || []) {
        rows.push(alert);
      }
    }
    return rows;
  }
  return [];
}

const ApplicationDetailPage = () => {
  const { appSlug: rawSlug } = useParams();
  const appSlug = decodeURIComponent(rawSlug || "");
  const [searchParams] = useSearchParams();
  const accountId = searchParams.get("accountId") || "";
  const navigate = useNavigate();
  const { user, accounts } = useAuth();
  const isViewer = !IsEnabled("Accounts");

  const selectedAccount = useMemo(() => {
    return (accounts || []).find(
      (a) => String(a.accountId || a.aws_account_id) === String(accountId)
    );
  }, [accounts, accountId]);

  const clientId = selectedAccount?.clientId || user?.client_id;
  const awsAccountId = accountId || selectedAccount?.accountId;

  const [app, setApp] = useState(null);
  const [loading, setLoading] = useState(false);
  const [onboardingScope, setOnboardingScope] = useState(null);
  const [logsMetricsTab, setLogsMetricsTab] = useState("logs");
  const [runtimeAlerts, setRuntimeAlerts] = useState([]);
  const [alertsLoading, setAlertsLoading] = useState(false);
  const [metricsModalTarget, setMetricsModalTarget] = useState(null);
  const [selectedMetricNames, setSelectedMetricNames] = useState([]);

  const loadDetail = useCallback(async () => {
    if (!clientId || !awsAccountId || !appSlug) return;
    setLoading(true);
    try {
      const res = await applicationDiscoveryService.getApplication(
        clientId,
        awsAccountId,
        appSlug
      );
      setApp(res.application || null);
    } catch (error) {
      console.error("[loadDetail]", error);
      message.error(error.message || "Failed to load application");
      setApp(null);
    } finally {
      setLoading(false);
    }
  }, [clientId, awsAccountId, appSlug]);

  const loadRuntimeAlerts = useCallback(async () => {
    if (!awsAccountId || !appSlug) return;
    setAlertsLoading(true);
    try {
      const payload = await alertApi.getFiringAlerts({
        awsAccountId,
        timeoutMs: 20000,
      });
      const all = normalizeFiringList(payload);
      const name = appDisplayName({ app_slug: appSlug, name: app?.name });
      const matched = all.filter((a) =>
        alertMatchesApplication(a, appSlug, name)
      );
      setRuntimeAlerts(matched);
    } catch (error) {
      console.error("[loadRuntimeAlerts]", error);
      setRuntimeAlerts([]);
    } finally {
      setAlertsLoading(false);
    }
  }, [awsAccountId, appSlug, app?.name]);

  useEffect(() => {
    loadDetail();
  }, [loadDetail]);

  useEffect(() => {
    if (app) loadRuntimeAlerts();
  }, [app, loadRuntimeAlerts]);

  const state = getApplicationOnboardingState(app || {});
  const serviceType = getDominantServiceType(app || {});

  const handleOnboard = async (scope, options = {}) => {
    if (!clientId || !awsAccountId || !appSlug) return;
    setOnboardingScope(scope);
    try {
      const result = await applicationDiscoveryService.onboardApplication(
        clientId,
        awsAccountId,
        appSlug,
        scope,
        options
      );
      const counts = result.pre_commit_target_count || {};
      const results = result.results || [];
      const failed = results.filter((r) => r.status === "failed");
      const ok = results.filter((r) => r.status === "onboarded" || r.status === "skipped");
      const selective =
        (options.logSelectors && options.logSelectors.length) ||
        (options.metricTargets && options.metricTargets.length);
      if (scope === "logs") {
        message.success(
          (selective ? "Log target onboard: " : "Logs onboard: ") +
            `${ok.length} succeeded` +
            (failed.length ? `, ${failed.length} failed` : "")
        );
      } else if (scope === "metrics") {
        message.success(
          (selective ? "Metric target onboard: " : "Metrics onboard: ") +
            `${ok.filter((r) => r.target !== "yace-sync").length} succeeded` +
            (failed.length ? `, ${failed.length} failed` : "")
        );
      } else {
        message.success(
          `Onboarded both (logs ${counts.logs ?? 0}, metrics ${counts.metrics ?? 0})` +
            (failed.length ? ` — ${failed.length} failed` : "")
        );
      }
      if (failed.length) {
        message.warning(failed.slice(0, 3).map((f) => f.reason || f.target).join("; "));
      }
      await loadDetail();
    } catch (error) {
      message.error(error.message || "Onboard failed");
    } finally {
      setOnboardingScope(null);
    }
  };

  const handleDeboard = async (scope, options = {}) => {
    if (!clientId || !awsAccountId || !appSlug) return;
    setOnboardingScope(scope);
    try {
      const result = await applicationDiscoveryService.deboardApplication(
        clientId,
        awsAccountId,
        appSlug,
        scope,
        options
      );
      const results = result.results || [];
      const failed = results.filter((r) => r.status === "failed");
      const ok = results.filter(
        (r) => r.status === "deboarded" || r.status === "skipped"
      );
      const selective =
        (options.logSelectors && options.logSelectors.length) ||
        (options.metricTargets && options.metricTargets.length);
      const label =
        scope === "logs"
          ? selective
            ? "Log target deboard"
            : "Logs deboard"
          : scope === "metrics"
            ? selective
              ? "Metric target deboard"
              : "Metrics deboard"
            : "Deboard";
      message.success(
        `${label}: ${ok.filter((r) => r.target !== "yace-sync").length} succeeded` +
          (failed.length ? `, ${failed.length} failed` : "")
      );
      if (failed.length) {
        message.warning(failed.slice(0, 3).map((f) => f.reason || f.target).join("; "));
      }
      await loadDetail();
    } catch (error) {
      message.error(error.message || "Deboard failed");
    } finally {
      setOnboardingScope(null);
    }
  };

  const openViewMetrics = (metricTarget) => {
    setMetricsModalTarget(metricTarget);
    setSelectedMetricNames([]);
  };

  const closeViewMetrics = () => {
    setMetricsModalTarget(null);
    setSelectedMetricNames([]);
  };

  const metricsModalAvailable = useMemo(() => {
    const rows = metricsModalTarget?.available_metrics || [];
    return rows
      .map((m) => (typeof m === "string" ? m : m?.name))
      .filter(Boolean);
  }, [metricsModalTarget]);

  const metricsModalOnboarded = useMemo(() => {
    return new Set(metricsModalTarget?.onboarded_metrics || []);
  }, [metricsModalTarget]);

  const metricsModalNewNames = useMemo(() => {
    return metricsModalAvailable.filter((n) => !metricsModalOnboarded.has(n));
  }, [metricsModalAvailable, metricsModalOnboarded]);

  const handleOnboardSelectedMetrics = async () => {
    if (!metricsModalTarget?.namespace || selectedMetricNames.length === 0) {
      message.warning("Select at least one metric to onboard");
      return;
    }
    await handleOnboard("metrics", {
      metricTargets: [
        {
          namespace: metricsModalTarget.namespace,
          dimensions: metricsModalTarget.dimensions || {},
          region: metricsModalTarget.region || "us-east-1",
          metrics: selectedMetricNames,
        },
      ],
    });
    closeViewMetrics();
  };

  const backToList = () => {
    const q = awsAccountId
      ? `?accountId=${encodeURIComponent(awsAccountId)}`
      : "";
    navigate(`/applications${q}`);
  };

  const goCreateAlert = () => {
    const params = new URLSearchParams();
    if (awsAccountId) params.set("accountId", awsAccountId);
    if (appSlug) params.set("application", appSlug);
    navigate(`/alerts?${params.toString()}`);
  };

  const overviewItems = [
    { label: "App slug", value: app?.app_slug || "—" },
    {
      label: "Coverage",
      value: (
        <Tag color={coverageTagColor(state.status)}>
          {coverageDisplayLabel(state.status)}
        </Tag>
      ),
    },
    {
      label: "Owner",
      value: app?.owner || "—",
    },
    {
      label: "Type",
      value: app?.type ? (
        <Tag color="purple">{app.type}</Tag>
      ) : serviceType ? (
        <Tag color={serviceTypeTagColor(serviceType)}>
          {formatServiceKey(serviceType)}
        </Tag>
      ) : (
        "—"
      ),
    },
    {
      label: "Environment",
      value: app?.environment || "—",
    },
    {
      label: "Resources",
      value: state.resourceCount || (app?.resources || []).length || 0,
    },
    {
      label: "Regions",
      value: (app?.regions || []).length
        ? (app.regions || []).map((r) => <Tag key={r}>{r}</Tag>)
        : "—",
    },
    {
      label: "Description",
      value: app?.description || "—",
      span: true,
    },
    {
      label: "Last discovered",
      value: app?.last_discovered_at
        ? String(app.last_discovered_at).replace("T", " ").slice(0, 19)
        : "—",
    },
    {
      label: "Summary",
      value: getResourceSummary(app || {}) || "—",
      span: true,
    },
  ];

  const logTargets = app?.log_targets || [];
  const metricTargets = app?.metric_targets || [];
  // Badges always follow row onboarded flags so counts match the table (e.g. 11/12).
  const logsOnboarded = logTargets.filter((t) => Boolean(t.onboarded)).length;
  const logsTotal = logTargets.length;
  const metricsOnboarded = metricTargets.filter((t) => Boolean(t.onboarded)).length;
  const metricsTotal = metricTargets.length;
  const onboardingLoading = onboardingScope != null;

  const collapseItems = [
    {
      key: "overview",
      label: (
        <Space>
          <AppstoreOutlined />
          <span>Overview</span>
        </Space>
      ),
      children: (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
            gap: "16px 24px",
          }}
        >
          {overviewItems.map((item) => (
            <div
              key={item.label}
              style={item.span ? { gridColumn: "1 / -1" } : undefined}
            >
              <Text type="secondary" style={{ fontSize: 12, display: "block" }}>
                {item.label}
              </Text>
              <div style={{ marginTop: 4 }}>{item.value}</div>
            </div>
          ))}
        </div>
      ),
    },
    {
      key: "resources",
      label: (
        <Space>
          <CloudServerOutlined />
          <span>Resources ({(app?.resources || []).length})</span>
        </Space>
      ),
      children: (
        <Table
          size="small"
          rowKey={(r) => r.arn || `${r.name}-${r.region}`}
          dataSource={app?.resources || []}
          pagination={{ pageSize: 10 }}
          locale={{ emptyText: "No resources in inventory" }}
          columns={[
            {
              title: "Name",
              key: "name",
              ellipsis: true,
              render: (_, r) => resourceDisplayName(r),
            },
            {
              title: "Service",
              key: "service",
              width: 160,
              render: (_, r) => {
                const sk = resolveResourceServiceKey(r);
                return (
                  <Tag color={serviceTypeTagColor(sk)}>{formatServiceKey(sk)}</Tag>
                );
              },
            },
            { title: "Region", dataIndex: "region", width: 120 },
            {
              title: "ARN",
              dataIndex: "arn",
              ellipsis: true,
              render: (a) =>
                a ? (
                  <Text code style={{ fontSize: 11 }}>
                    {a}
                  </Text>
                ) : (
                  "—"
                ),
            },
          ]}
        />
      ),
    },
    {
      key: "logs-metrics",
      label: (
        <Space>
          <FileTextOutlined />
          <span>Logs & Metrics</span>
        </Space>
      ),
      children: (
        <div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
              marginBottom: 16,
              flexWrap: "wrap",
            }}
          >
            <div
              style={{
                display: "inline-flex",
                gap: 8,
                padding: 4,
                borderRadius: 10,
                background: "rgba(0,0,0,0.04)",
              }}
            >
              <Button
                type={logsMetricsTab === "logs" ? "default" : "text"}
                icon={<FileTextOutlined />}
                onClick={() => setLogsMetricsTab("logs")}
                style={{
                  borderRadius: 8,
                  fontWeight: logsMetricsTab === "logs" ? 600 : 400,
                  background: logsMetricsTab === "logs" ? "#fff" : "transparent",
                  boxShadow: logsMetricsTab === "logs" ? "0 1px 2px rgba(0,0,0,0.08)" : "none",
                }}
              >
                Logs ({logsOnboarded}/{logsTotal})
              </Button>
              <Button
                type={logsMetricsTab === "metrics" ? "default" : "text"}
                icon={<LineChartOutlined />}
                onClick={() => setLogsMetricsTab("metrics")}
                style={{
                  borderRadius: 8,
                  fontWeight: logsMetricsTab === "metrics" ? 600 : 400,
                  background: logsMetricsTab === "metrics" ? "#fff" : "transparent",
                  boxShadow:
                    logsMetricsTab === "metrics" ? "0 1px 2px rgba(0,0,0,0.08)" : "none",
                }}
              >
                Metrics ({metricsOnboarded}/{metricsTotal})
              </Button>
            </div>
            <Button
              type="primary"
              disabled={
                isViewer ||
                onboardingLoading ||
                (logsMetricsTab === "logs"
                  ? logsOnboarded >= logsTotal || logsTotal === 0
                  : metricsOnboarded >= metricsTotal || metricsTotal === 0)
              }
              loading={
                onboardingScope === (logsMetricsTab === "logs" ? "logs" : "metrics") ||
                onboardingScope === "both"
              }
              onClick={() =>
                handleOnboard(logsMetricsTab === "logs" ? "logs" : "metrics")
              }
            >
              {logsMetricsTab === "logs" ? "Onboard all logs" : "Onboard all metrics"}
            </Button>
          </div>

          {logsMetricsTab === "logs" ? (
            <Table
              size="small"
              rowKey={(r) => r.selector}
              dataSource={logTargets}
              pagination={{ pageSize: 8 }}
              locale={{ emptyText: "No log targets" }}
              expandable={{
                expandedRowRender: (r) => (
                  <Space direction="vertical" size={2}>
                    <Text type="secondary">
                      Confidence:{" "}
                      {r.confidence != null ? Number(r.confidence).toFixed(2) : "—"}
                    </Text>
                    <Text type="secondary">Reason: {r.reason || "—"}</Text>
                  </Space>
                ),
                rowExpandable: (r) => Boolean(r.reason || r.confidence != null),
              }}
              columns={[
                {
                  title: "Enabled",
                  key: "enabled",
                  width: 90,
                  render: (_, record) => {
                    const enabled = Boolean(record.onboarded);
                    const switchEl = (
                      <Switch
                        size="small"
                        checked={enabled}
                        disabled={isViewer || onboardingLoading}
                        loading={onboardingScope === "logs"}
                        onChange={(checked) => {
                          if (!record.selector) return;
                          if (checked && !enabled) {
                            handleOnboard("logs", {
                              logSelectors: [record.selector],
                            });
                          } else if (!checked && enabled) {
                            handleDeboard("logs", {
                              logSelectors: [record.selector],
                            });
                          }
                        }}
                      />
                    );
                    return (
                      <Tooltip
                        title={
                          enabled
                            ? "Disable to deboard this log target"
                            : "Enable to onboard only this log target"
                        }
                      >
                        {switchEl}
                      </Tooltip>
                    );
                  },
                },
                {
                  title: "Log Name",
                  key: "name",
                  width: 200,
                  ellipsis: true,
                  render: (_, r) => (
                    <Text strong>{logTargetDisplayName(r.selector)}</Text>
                  ),
                },
                {
                  title: "Source",
                  key: "source",
                  width: 120,
                  render: (_, r) => {
                    const label = logTargetSourceLabel(r.kind);
                    return (
                      <Tag color={label === "S3" ? "gold" : "blue"}>{label}</Tag>
                    );
                  },
                },
                {
                  title: "Location",
                  key: "location",
                  ellipsis: true,
                  render: (_, r) => (
                    <Text
                      code
                      style={{ fontSize: 11, wordBreak: "break-all" }}
                      copyable={r.selector ? { text: r.selector } : false}
                    >
                      {r.selector || "—"}
                    </Text>
                  ),
                },
              ]}
            />
          ) : (
            <Table
              size="small"
              rowKey={(r, i) =>
                `${r.namespace}-${r.region}-${JSON.stringify(r.dimensions || {})}-${i}`
              }
              dataSource={metricTargets}
              pagination={{ pageSize: 8 }}
              locale={{ emptyText: "No metric targets" }}
              expandable={{
                expandedRowRender: (r) => (
                  <Space direction="vertical" size={2}>
                    <Text type="secondary">
                      Confidence:{" "}
                      {r.confidence != null ? Number(r.confidence).toFixed(2) : "—"}
                    </Text>
                    <Text type="secondary">Reason: {r.reason || "—"}</Text>
                    {r.dimensions && Object.keys(r.dimensions).length > 0 ? (
                      <Text type="secondary" code style={{ fontSize: 11 }}>
                        {JSON.stringify(r.dimensions)}
                      </Text>
                    ) : null}
                  </Space>
                ),
                rowExpandable: () => true,
              }}
              columns={[
                {
                  title: "Enabled",
                  key: "enabled",
                  width: 90,
                  render: (_, record) => {
                    const enabled = Boolean(record.onboarded);
                    const switchEl = (
                      <Switch
                        size="small"
                        checked={enabled}
                        disabled={isViewer || onboardingLoading}
                        loading={onboardingScope === "metrics"}
                        onChange={(checked) => {
                          if (!record.namespace) return;
                          const target = {
                            namespace: record.namespace,
                            dimensions: record.dimensions || {},
                            region: record.region || "us-east-1",
                          };
                          if (checked && !enabled) {
                            // Pipeline-style: pick metric names instead of scraping all defaults.
                            openViewMetrics(record);
                          } else if (!checked && enabled) {
                            handleDeboard("metrics", {
                              metricTargets: [target],
                            });
                          }
                        }}
                      />
                    );
                    return (
                      <Tooltip
                        title={
                          enabled
                            ? "Disable to deboard this metric target"
                            : "Enable to choose which metrics to onboard"
                        }
                      >
                        {switchEl}
                      </Tooltip>
                    );
                  },
                },
                {
                  title: "Metric",
                  key: "metric",
                  ellipsis: true,
                  render: (_, r) => <Text strong>{metricTargetDisplayName(r)}</Text>,
                },
                {
                  title: "Source",
                  key: "source",
                  width: 120,
                  render: () => <Tag color="blue">CloudWatch</Tag>,
                },
                {
                  title: "Region",
                  dataIndex: "region",
                  width: 120,
                  render: (r) => r || "—",
                },
                {
                  title: "Metrics",
                  key: "view",
                  width: 120,
                  render: (_, record) => (
                    <Tooltip title="View and selectively onboard CloudWatch metrics">
                      <Button
                        type="link"
                        size="small"
                        icon={<LineChartOutlined />}
                        onClick={() => openViewMetrics(record)}
                      >
                        View
                      </Button>
                    </Tooltip>
                  ),
                },
              ]}
            />
          )}
        </div>
      ),
    },
    {
      key: "runtime-alerts",
      label: (
        <Space>
          <BellOutlined />
          <span>Runtime Alerts</span>
          <Tag>{runtimeAlerts.length}</Tag>
        </Space>
      ),
      children: (
        <div>
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              marginBottom: 12,
              gap: 8,
            }}
          >
            <Button
              icon={<ReloadOutlined />}
              loading={alertsLoading}
              onClick={loadRuntimeAlerts}
            >
              Refresh
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              disabled={isViewer}
              onClick={goCreateAlert}
            >
              Create Runtime Alert
            </Button>
          </div>
          <Table
            size="small"
            loading={alertsLoading}
            rowKey={(r, i) =>
              r.fingerprint ||
              r.labels?.alertname ||
              r.alertname ||
              r.name ||
              String(i)
            }
            dataSource={runtimeAlerts}
            pagination={{ pageSize: 8 }}
            locale={{
              emptyText:
                "No firing alerts labeled for this application. Create an alert with label application=<app slug>, or check Alerts.",
            }}
            columns={[
              {
                title: "Alert",
                key: "name",
                render: (_, r) =>
                  r.labels?.alertname ||
                  r.alertname ||
                  r.name ||
                  r.labels?.alertname ||
                  "—",
              },
              {
                title: "Severity",
                key: "severity",
                width: 110,
                render: (_, r) => {
                  const sev = r.labels?.severity || r.severity || "—";
                  const color =
                    sev === "critical"
                      ? "error"
                      : sev === "warning"
                        ? "warning"
                        : "default";
                  return <Tag color={color}>{sev}</Tag>;
                },
              },
              {
                title: "State",
                key: "state",
                width: 100,
                render: (_, r) => {
                  const st =
                    r.state ||
                    r.status?.state ||
                    r.status ||
                    "firing";
                  return <Tag color="error">{String(st)}</Tag>;
                },
              },
              {
                title: "Summary",
                key: "summary",
                ellipsis: true,
                render: (_, r) =>
                  r.annotations?.summary ||
                  r.annotations?.description ||
                  r.summary ||
                  "—",
              },
            ]}
          />
        </div>
      ),
    },
  ];

  return (
    <div
      style={{
        width: "100%",
        maxWidth: 1100,
        margin: "0 auto",
        padding: "8px 14px",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          marginBottom: 16,
          flexWrap: "wrap",
        }}
      >
        <Title level={4} style={{ margin: 0, flex: 1, minWidth: 160 }}>
          {loading ? "Loading…" : appDisplayName(app || { app_slug: appSlug })}
        </Title>
        <Button icon={<ArrowLeftOutlined />} onClick={backToList}>
          Back
        </Button>
        <Button
          icon={<ReloadOutlined />}
          loading={loading}
          onClick={() => {
            loadDetail();
            loadRuntimeAlerts();
          }}
          disabled={!awsAccountId}
        >
          Refresh
        </Button>
      </div>

      {!awsAccountId && (
        <Text type="secondary">
          Missing accountId. Open this application from the Applications list.
        </Text>
      )}

      {awsAccountId && !loading && !app && (
        <Text type="secondary">Application not found in inventory.</Text>
      )}

      {app && (
        <Collapse
          defaultActiveKey={[
            "overview",
            "resources",
            "logs-metrics",
            "runtime-alerts",
          ]}
          items={collapseItems}
          style={{ background: "transparent" }}
        />
      )}

      <Modal
        title={
          metricsModalTarget
            ? `Metrics — ${metricTargetDisplayName(metricsModalTarget)}`
            : "Metrics"
        }
        open={Boolean(metricsModalTarget)}
        onCancel={closeViewMetrics}
        destroyOnClose
        width={560}
        footer={[
          <Button key="close" onClick={closeViewMetrics}>
            Close
          </Button>,
          <Button
            key="select-all"
            disabled={isViewer || metricsModalNewNames.length === 0}
            onClick={() => setSelectedMetricNames([...metricsModalNewNames])}
          >
            Select all new
          </Button>,
          <Button
            key="onboard"
            type="primary"
            loading={onboardingScope === "metrics"}
            disabled={
              isViewer ||
              selectedMetricNames.length === 0 ||
              onboardingLoading
            }
            onClick={handleOnboardSelectedMetrics}
          >
            Onboard selected ({selectedMetricNames.length})
          </Button>,
        ]}
      >
        {metricsModalAvailable.length === 0 ? (
          <Text type="secondary">
            No default metrics catalog for namespace{" "}
            {metricsModalTarget?.namespace || "—"}.
          </Text>
        ) : (
          <div style={{ maxHeight: 320, overflowY: "auto" }}>
            {metricsModalAvailable.map((name) => {
              const already = metricsModalOnboarded.has(name);
              return (
                <div
                  key={name}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "6px 0",
                    borderBottom: "1px solid #f0f0f0",
                  }}
                >
                  {already ? (
                    <span style={{ width: 16 }} />
                  ) : (
                    <Checkbox
                      checked={selectedMetricNames.includes(name)}
                      disabled={isViewer}
                      onChange={(e) => {
                        setSelectedMetricNames((prev) =>
                          e.target.checked
                            ? [...prev, name]
                            : prev.filter((n) => n !== name)
                        );
                      }}
                    />
                  )}
                  <Text code style={{ flex: 1 }}>
                    {metricsModalTarget?.namespace}/{name}
                  </Text>
                  {already ? (
                    <Tag color="green">Onboarded</Tag>
                  ) : (
                    <Tag color="blue">New</Tag>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default ApplicationDetailPage;
